<div class="setup-page">
  <div class="breadcrumb">
    <span [routerLink]="['/app/dashboard-v2']">Dashboard</span>
    <span class="sep">/</span>
    <span>Settings</span>
    <span class="sep">/</span>
    <span class="current">Setup</span>
  </div>

  <ng-container *ngIf="isLoadingStatus">
    <div class="setup-header-card">
      <div class="setup-header-top">
        <div>
          <p-skeleton width="220px" height="20px" styleClass="mb-2"></p-skeleton>
          <p-skeleton width="320px" height="13px"></p-skeleton>
        </div>
        <p-skeleton width="140px" height="24px" borderRadius="999px"></p-skeleton>
      </div>
      <p-skeleton width="100%" height="8px" styleClass="mt-3" borderRadius="999px"></p-skeleton>
    </div>

    <div class="setup-section" *ngFor="let skeletonRow of [1,2,3,4,5]">
      <div class="setup-section-header">
        <div class="setup-section-head-left">
          <p-skeleton class="setup-step-index" shape="circle" size="24px"></p-skeleton>
          <div>
            <p-skeleton class="setup-section-title" width="160px" height="14px" styleClass="mb-2"></p-skeleton>
            <p-skeleton class="setup-section-desc" width="220px" height="12px"></p-skeleton>
          </div>
        </div>
        <div class="setup-section-head-right">
          <p-skeleton width="72px" height="22px" borderRadius="999px"></p-skeleton>
        </div>
      </div>
    </div>
  </ng-container>

  <ng-container *ngIf="!isLoadingStatus">
  <div class="setup-header-card">
    <div class="setup-header-top">
      <div>
        <div class="setup-title">Business Setup</div>
        <div class="setup-subtitle">Finish these steps to get your business selling on LincSell.</div>
      </div>
      <div class="setup-count-badge">{{ completedCount }} of {{ totalCount }} complete</div>
    </div>
    <div class="setup-progress-track">
      <div class="setup-progress-fill" [style.width.%]="progressPercent"></div>
    </div>
    <div class="setup-progress-note">Showing setup steps for your selected sales channels.</div>
  </div>

  <div class="setup-section" [class.open]="isOpen('channels')">
    <div class="setup-section-header" (click)="toggleSection('channels')">
      <div class="setup-section-head-left">
        <span class="setup-step-index">{{ channelsStepNumber }}.</span>
        <div>
          <div class="setup-section-title">Sales channels</div>
          <div class="setup-section-desc">{{ channelLabel }}</div>
        </div>
      </div>
      <div class="setup-section-head-right">
        <span class="status-pill" [class.complete]="channelsComplete" [class.in-progress]="channelsInProgress">{{ channelsComplete ? 'Complete' : (channelsInProgress ? 'In Progress' : 'Pending') }}</span>
        <i class="pi" [ngClass]="isOpen('channels') ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
      </div>
    </div>
    <div class="setup-section-body" *ngIf="isOpen('channels')">
      <p class="setup-help-text">
        Switch between in-store, online store, or both at any time. Saving updates the steps below to match your channels.
      </p>
      <div class="channel-options">
        <div class="channel-option" [class.selected]="sellOnline" (click)="toggleSellOnline()">
          <span class="channel-check" [class.checked]="sellOnline">
            <i class="pi pi-check" *ngIf="sellOnline"></i>
          </span>
          <div>
            <div class="channel-title">Online Store</div>
            <div class="channel-desc">Sell from your own LincSell storefront.</div>
          </div>
        </div>
        <div class="channel-option" [class.selected]="sellInStore" (click)="toggleSellInStore()">
          <span class="channel-check" [class.checked]="sellInStore">
            <i class="pi pi-check" *ngIf="sellInStore"></i>
          </span>
          <div>
            <div class="channel-title">In-store</div>
            <div class="channel-desc">Sell at your counter with LincSell POS.</div>
          </div>
        </div>
      </div>
      <div class="pos-hardware-dropdown" *ngIf="sellInStore">
        <label>POS hardware <span class="required">*</span></label>
        <p-dropdown
          [options]="visiblePosHardwareOptions"
          [(ngModel)]="posHardware"
          optionLabel="label"
          optionValue="value"
          placeholder="Select an option"
          [showClear]="true"
          appendTo="body"
          [autoZIndex]="true"
          [baseZIndex]="1200"
          styleClass="pos-hardware-select"
          panelStyleClass="pos-hardware-panel"
        >
          <ng-template let-option pTemplate="item">
            <div class="pos-hardware-card">
              <i
                class="pi pi-check"
                [class.visible]="posHardware === option.value"
                aria-hidden="true"
              ></i>
              <span>{{ option.label }}</span>
            </div>
          </ng-template>
        </p-dropdown>
        <p class="setup-error-note" *ngIf="posHardwareRequired">Select one POS hardware option to save.</p>

        <div class="pos-hardware-info-card" *ngIf="posHardware === 'own'">
          <div class="pos-hardware-info-title">Install LincSell POS on your device</div>
          <p class="pos-hardware-info-desc">Download the app on the phone or tablet you will use at the counter, then sign in with this account to start taking payments.</p>
          <div class="pos-hardware-store-badges">
            <a class="pos-hardware-store-badge" href="https://play.google.com/store/apps/details?id=com.lincsell.com.pos" target="_blank" rel="noopener noreferrer">
              <i class="pi pi-android"></i>
              <span>
                <small>GET IT ON</small>
                <strong>Google Play</strong>
              </span>
            </a>
            <a class="pos-hardware-store-badge" href="https://apps.apple.com/us/app/lincsell-pos-pro/id6476977214" target="_blank" rel="noopener noreferrer">
              <i class="pi pi-apple"></i>
              <span>
                <small>DOWNLOAD ON THE</small>
                <strong>App Store</strong>
              </span>
            </a>
          </div>
        </div>

        <div class="pos-hardware-info-card" *ngIf="posHardware === 'buy'">
          <div class="pos-hardware-info-title">Buy LincSell Supported Hardware</div>
          <p class="pos-hardware-info-desc">Pick registers, card readers, printers and accessories. Hardware is charged today and ships separately from your subscription.</p>
          <button pButton type="button" label="Buy Hardware" class="pos-hardware-buy-btn" (click)="goToHardwareShop()"></button>
        </div>
      </div>
      <p class="setup-help-text">Select the channels you sell through. Choose "Both" to cover in-store and online store together.</p>
      <p class="setup-error-note" *ngIf="noChannelSelected">Select at least one channel — Online Store, In-store, or both — to save.</p>
      <button pButton type="button" class="setup-save-btn" [loading]="isSavingChannels" [label]="isSavingChannels ? 'Saving…' : (channelsComplete ? 'Save' : 'Save and Next')" [disabled]="!canSaveChannels" (click)="saveChannels()"></button>
    </div>
  </div>

  <div class="setup-section" [class.open]="isOpen('business')">
    <div class="setup-section-header" (click)="toggleSection('business')">
      <div class="setup-section-head-left">
        <span class="setup-step-index">{{ businessStepNumber }}.</span>
        <div>
          <div class="setup-section-title">Business information</div>
          <div class="setup-section-desc">Address used on receipts, invoices and tax reports.</div>
        </div>
      </div>
      <div class="setup-section-head-right">
        <span class="status-pill" [class.complete]="businessComplete">{{ businessComplete ? 'Complete' : 'Pending' }}</span>
        <i class="pi" [ngClass]="isOpen('business') ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
      </div>
    </div>
    <div class="setup-section-body" *ngIf="isOpen('business')">
      <p class="setup-help-text" *ngIf="businessComplete">
        Your business information has been saved and can't be edited here. Contact us if something needs to change.
      </p>
      <div class="setup-form-grid">
        <label class="setup-field span-2">
          <span>Business name <span class="required">*</span></span>
          <input pInputText type="text" [(ngModel)]="businessName" placeholder="Your business name" [readonly]="businessComplete" />
        </label>
        <label class="setup-field span-2">
          <span>Business type <span class="required">*</span></span>
          <p-dropdown
            [options]="businessTypes"
            [(ngModel)]="businessType"
            optionLabel="name"
            optionValue="id"
            placeholder="Select your industry"
            [filter]="true"
            filterBy="name"
            filterPlaceholder="Search industry"
            appendTo="body"
            [autoZIndex]="true"
            styleClass="setup-dropdown w-full"
            [disabled]="businessComplete"
          ></p-dropdown>
        </label>
        <label class="setup-field span-2">
          <span>Tell us about your business <span class="required">*</span></span>
          <textarea class="setup-textarea" [(ngModel)]="businessAbout" placeholder="Write a short paragraph about what you sell, who your customers are, and what makes your business unique." [readonly]="businessComplete"></textarea>
        </label>
        <label class="setup-field span-2">
          <span>Street address <span class="required">*</span></span>
          <input pInputText type="text" [(ngModel)]="streetAddress" placeholder="123 Main Street, Suite 400" [readonly]="businessComplete" />
        </label>
        <label class="setup-field span-2">
          <span>Address 2 <span class="setup-optional">(optional)</span></span>
          <input pInputText type="text" [(ngModel)]="address2" placeholder="Apartment, suite, unit, floor" [readonly]="businessComplete" />
        </label>
        <label class="setup-field">
          <span>City <span class="required">*</span></span>
          <input pInputText type="text" [(ngModel)]="city" placeholder="Austin" [readonly]="businessComplete" />
        </label>
        <label class="setup-field">
          <span>State / Province <span class="required">*</span></span>
          <p-dropdown
            [options]="usStates"
            [(ngModel)]="state"
            optionLabel="name"
            optionValue="code"
            placeholder="Select State"
            [filter]="true"
            filterBy="name,code"
            filterPlaceholder="Search states"
            ariaFilterLabel="Search states"
            [resetFilterOnHide]="true"
            appendTo="body"
            [autoZIndex]="true"
            [baseZIndex]="1200"
            [showClear]="true"
            styleClass="setup-dropdown w-full"
            panelStyleClass="state-province-panel"
            [disabled]="businessComplete"
          ></p-dropdown>
        </label>
        <label class="setup-field">
          <span>ZIP / Postal code <span class="required">*</span></span>
          <input pInputText type="text" [(ngModel)]="zip" placeholder="78701" maxlength="10" [readonly]="businessComplete" />
        </label>
        <!-- Country hidden — LincSell only supports US today; this.country stays 'US'. TODO re-enable if multi-country support ships. -->
        <label class="setup-field" *ngIf="false">
          <span>Country <span class="required">*</span></span>
          <input pInputText type="text" [(ngModel)]="country" placeholder="United States" [readonly]="businessComplete" />
        </label>
      </div>
      <button
        *ngIf="!businessComplete"
        pButton
        type="button"
        class="setup-save-btn"
        [loading]="isSavingBusinessInfo"
        [label]="isSavingBusinessInfo ? 'Saving…' : 'Next'"
        [disabled]="!canSaveBusinessInfo"
        (click)="saveBusinessInfo()"
      ></button>
    </div>
  </div>

  <div class="setup-section" [class.open]="isOpen('domain')" [class.disabled]="!businessComplete" *ngIf="domainStepNeeded">
    <div class="setup-section-header" (click)="toggleSection('domain')">
      <div class="setup-section-head-left">
        <span class="setup-step-index">{{ domainStepNumber }}.</span>
        <div>
          <div class="setup-section-title">Domain &amp; store template</div>
          <div class="setup-section-desc" *ngIf="businessComplete">Set up your domain and choose a template for your online store.</div>
          <div class="setup-section-desc" *ngIf="!businessComplete">Finish "Business information" first to unlock this step.</div>
        </div>
      </div>
      <div class="setup-section-head-right">
        <span class="status-pill" [class.complete]="domainComplete" *ngIf="businessComplete">{{ domainComplete ? 'Complete' : 'Pending' }}</span>
        <i class="pi pi-lock" *ngIf="!businessComplete"></i>
        <i class="pi" *ngIf="businessComplete" [ngClass]="isOpen('domain') ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
      </div>
    </div>
    <div class="setup-section-body domain-setup-body" *ngIf="isOpen('domain')">
      <label class="setup-field">
        <span>Free LincSell subdomain <span class="required">*</span></span>
        <div class="subdomain-row">
          <input pInputText type="text" [ngModel]="subdomain" (input)="onSubdomainInput($event)" (blur)="onSubdomainBlur()" placeholder="yourbrand" [disabled]="domainComplete || isDomainSectionBusy" />
          <span class="subdomain-suffix" *ngIf="!isAiEditorStore">.lincsell.com</span>
        </div>
        <ng-container *ngIf="!domainComplete">
          <small class="subdomain-hint error" *ngIf="subdomainTooShort">At least {{ minSubdomainLength }} characters.</small>
          <small class="subdomain-hint checking" *ngIf="!subdomainTooShort && isCheckingDomainAvailability">Checking availability...</small>
          <small class="subdomain-hint success" *ngIf="!isCheckingDomainAvailability && domainAvailable === true">This store name is available!</small>
          <small class="subdomain-hint error" *ngIf="!isCheckingDomainAvailability && domainAvailable === false">{{ domainUnavailableReason || 'This store name is not available. Please choose a different one.' }}</small>
        </ng-container>
      </label>

      <div class="online-store-modal-field brand-colors-field" *ngIf="!domainComplete">
        <span>Brand colors <span class="required">*</span></span>
        <p-multiSelect
          [options]="brandColorOptions"
          [(ngModel)]="selectedBrandColors"
          [disabled]="isDomainSectionBusy"
          optionLabel="name"
          optionValue="code"
          defaultLabel="Select brand colors"
          display="chip"
          [filter]="true"
          filterBy="name"
          appendTo="body"
          styleClass="brand-color-multiselect"
          panelStyleClass="brand-color-multiselect-panel"
        >
          <ng-template let-colors pTemplate="selectedItems">
            <ng-container *ngIf="colors?.length; else brandColorPlaceholder">
              <span
                class="brand-color-dropdown-chip"
                *ngFor="let colorCode of colors"
              >
                <span class="brand-color-dot" [style.background-color]="getBrandColorOption(colorCode)?.hex"></span>
                <span>{{ getBrandColorOption(colorCode)?.name }}</span>
                <button
                  type="button"
                  class="brand-color-chip-remove"
                  [attr.aria-label]="'Remove ' + getBrandColorOption(colorCode)?.name"
                  (click)="removeSelectedBrandColor(colorCode, $event)"
                ><i class="pi pi-times"></i></button>
              </span>
            </ng-container>
            <ng-template #brandColorPlaceholder>Select brand colors</ng-template>
          </ng-template>
          <ng-template let-color pTemplate="item">
            <span class="brand-color-option">
            <span class="brand-color-swatch" [style.background]="color.hex"></span>
            <span class="brand-color-name">{{ color.name }}</span>
            </span>
          </ng-template>
        </p-multiSelect>
      </div>

      <div class="online-store-modal-field brand-colors-field" *ngIf="domainComplete">
        <span>Brand colors <span class="required">*</span></span>
        <div class="brand-color-readonly-list" *ngIf="selectedBrandColors.length; else brandColorReadonlyEmpty">
          <span
            class="brand-color-selected-chip"
            *ngFor="let colorCode of selectedBrandColors"
            [style.background-color]="getBrandColorOption(colorCode)?.hex"
            [style.color]="getBrandColorTextColor(colorCode)"
          >{{ getBrandColorOption(colorCode)?.name }}</span>
        </div>
        <ng-template #brandColorReadonlyEmpty>
          <span class="brand-color-readonly-empty">No brand colors saved.</span>
        </ng-template>
      </div>

      <div class="online-store-modal-field store-template-field">
        <span>Online store setup <span class="required">*</span></span>
      </div>

      <section class="selected-store-template" *ngIf="selectedTemplate; else chooseTemplateState" aria-label="Selected online store template">
        <div
          class="selected-store-template-preview"
          role="img"
          [attr.aria-label]="selectedTemplate.name + ' store template preview'"
          [style.background-image]="'url(' + selectedTemplate.image + ')'"
        ></div>
        <div class="selected-store-template-footer">
          <div class="selected-store-template-name">
            <span>Template selected</span>
            <strong>{{ selectedTemplate.name }}</strong>
          </div>
          <button
            pButton
            type="button"
            icon="pi pi-th-large"
            label="Change template"
            class="p-button-outlined change-store-template-button"
            *ngIf="!domainComplete"
            [disabled]="isDomainSectionBusy"
            (click)="openOnlineStoreInformation()"
          ></button>
        </div>
      </section>

      <ng-template #chooseTemplateState>
        <button
          pButton
          type="button"
          icon="pi pi-th-large"
          label="Choose template"
          class="choose-store-template-button"
          *ngIf="!domainComplete"
          [disabled]="isDomainSectionBusy"
          (click)="openOnlineStoreInformation()"
        ></button>
      </ng-template>

      <section class="store-created-status" *ngIf="domainComplete" aria-label="Online store status">
        <div class="store-created-actions">
          <button
            pButton
            type="button"
            icon="pi pi-eye"
            label="Preview online store"
            class="p-button-outlined preview-store-button"
            [disabled]="!canPreviewStore"
            (click)="previewOnlineStore()"
          ></button>
          <button
            pButton
            type="button"
            icon="pi pi-pencil"
            label="Edit online store"
            class="edit-store-button"
            [disabled]="!canEditStore"
            (click)="goToStoreEditor()"
          ></button>
        </div>
        <p class="store-created-status-note" *ngIf="isCustomStorePending">Our team is building your custom online store. It will be ready within the next 3 business days, and we&rsquo;ll contact you at your business email address once it&rsquo;s ready. The &ldquo;Preview Online Store&rdquo; and &ldquo;Edit Online Store&rdquo; buttons will also be enabled at that time.</p>
        <p class="store-created-status-note" *ngIf="isMobileDevice && !isCustomStorePending">The store editor isn&rsquo;t available on mobile. Please use a desktop or laptop to edit your online store.</p>
        <p class="store-created-status-note" *ngIf="!isCustomStorePending">Your store template and domain are locked in and can no longer be changed here. Contact us if you need a custom build.</p>
      </section>

      <p class="store-template-warning" *ngIf="showStoreCreationActions && !domainComplete">Choose carefully - your store template cannot be changed once your store is created.</p>

      <div class="terms-consent-field" *ngIf="showStoreCreationActions && !domainComplete">
        <p-checkbox
          [binary]="true"
          [(ngModel)]="termsAccepted"
          [disabled]="isDomainSectionBusy"
          inputId="domainStepTerms"
        ></p-checkbox>
        <label for="domainStepTerms">
          I agree to the <a href="https://lincsell.com/terms-of-service/" target="_blank" rel="noopener">Terms &amp; Conditions</a> and <a href="https://lincsell.com/privacy-policy/" target="_blank" rel="noopener">Privacy Policy</a>, including prohibited use, restricted products/services, and policy violations.
        </label>
      </div>

      <div class="store-creation-actions" *ngIf="showStoreCreationActions && !domainComplete">
      <button
        pButton
        type="button"
        icon="pi pi-check"
        class="create-store-button"
        [loading]="isSavingDomain"
        [label]="isSavingDomain ? 'Creating store...' : 'Create store'"
        [disabled]="!canSaveDomain"
        (click)="saveDomain()"
      ></button>
        <button
          pButton
          type="button"
          class="custom-online-store-button"
          label="Contact us for a custom online store"
          [loading]="isSubmittingCustomStoreRequest"
          [disabled]="!canRequestCustomOnlineStore"
          (click)="requestCustomOnlineStore()"
        ></button>
      </div>
      <p class="store-creation-help" *ngIf="showStoreCreationActions && !domainComplete">Create your store to publish it, or contact us for a fully custom build.</p>

      <button
        pButton
        type="button"
        label="Next"
        class="domain-next-button"
        [disabled]="!domainComplete"
        (click)="continueFromDomainSetup()"
      ></button>
    </div>
  </div>

  <div class="setup-section" [class.open]="isOpen('product')">
    <div class="setup-section-header" (click)="toggleSection('product')">
      <div class="setup-section-head-left">
        <span class="setup-step-index">{{ productStepNumber }}.</span>
        <div>
          <div class="setup-section-title">Add Product</div>
          <div class="setup-section-desc">Add a product so you're ready to start selling.</div>
        </div>
      </div>
      <div class="setup-section-head-right">
        <span class="status-pill" [class.complete]="productsComplete">{{ productsComplete ? 'Complete' : 'Pending' }}</span>
        <i class="pi" [ngClass]="isOpen('product') ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
      </div>
    </div>
    <div class="setup-section-body" *ngIf="isOpen('product')">
      <div class="setup-success-banner" *ngIf="productsComplete" role="status">
        <i class="pi pi-check-circle"></i>
        <div>
          <div class="setup-success-title">Product added</div>
          <div class="setup-success-text">You already added a product. You can add more below or head to the full product catalog.</div>
        </div>
      </div>
      <div class="setup-form-grid">
        <label class="setup-field span-2">
          <span>Product Name <span class="required">*</span></span>
          <input pInputText type="text" [(ngModel)]="productName" placeholder="e.g. Summer T-Shirt" />
        </label>
        <label class="setup-field span-2">
          <span>Description <span class="required">*</span></span>
          <textarea class="setup-textarea" [(ngModel)]="productDescription" placeholder="Describe your product — materials, features, and what makes it special."></textarea>
        </label>
        <label class="setup-field span-2">
          <span>Category <span class="required">*</span></span>
          <input pInputText type="text" [(ngModel)]="productCategory" placeholder="e.g. Clothing" />
        </label>
        <label class="setup-field">
          <span>Price ($) <span class="required">*</span></span>
          <p-inputNumber
            [(ngModel)]="productPrice"
            mode="currency"
            currency="USD"
            locale="en-US"
            placeholder="0.00"
            styleClass="setup-input-number w-full"
            [inputStyleClass]="'w-full'"
          ></p-inputNumber>
        </label>
        <label class="setup-field">
          <span>Quantity <span class="required">*</span></span>
          <p-inputNumber
            [(ngModel)]="productQuantity"
            [min]="0"
            placeholder="1"
            styleClass="setup-input-number w-full"
            [inputStyleClass]="'w-full'"
          ></p-inputNumber>
        </label>
        <div class="setup-field span-2">
          <span>Product Image</span>
          <div class="image-dropzone" *ngIf="!productImagePreview" (click)="productImageInput.click()">
            <i class="pi pi-upload"></i>
            <span class="image-dropzone-title">Click to upload an image</span>
            <span class="image-dropzone-hint">PNG, JPG, WEBP, GIF, BMP or AVIF — up to 5MB</span>
            <input #productImageInput type="file" accept=".png,.jpg,.jpeg,.webp,.gif,.bmp,.avif" (change)="onProductImageSelected($event)" style="display: none" />
          </div>
          <div class="image-preview-container" *ngIf="productImagePreview">
            <img [src]="productImagePreview" alt="Preview" class="image-preview" />
            <button type="button" class="btn-remove-image" (click)="removeProductImage()">×</button>
          </div>
        </div>
      </div>
      <button
        pButton
        type="button"
        class="setup-save-btn"
        [loading]="isSavingProduct"
        [label]="isSavingProduct ? 'Saving…' : (productsComplete ? 'Save Another Product' : 'Save Product')"
        [disabled]="!canSaveProduct"
        (click)="saveProduct()"
      ></button>
    </div>
  </div>

  <div class="setup-section" [class.open]="isOpen('shipping')" *ngIf="shippingStepNeeded">
    <div class="setup-section-header" (click)="toggleSection('shipping')">
      <div class="setup-section-head-left">
        <span class="setup-step-index">{{ shippingStepNumber }}.</span>
        <div>
          <div class="setup-section-title">Shipping</div>
          <div class="setup-section-desc">Delivery and pickup options for online orders.</div>
        </div>
      </div>
      <div class="setup-section-head-right">
        <span class="status-pill" [class.complete]="shippingComplete">{{ shippingComplete ? 'Complete' : 'Pending' }}</span>
        <i class="pi" [ngClass]="isOpen('shipping') ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
      </div>
    </div>
    <div class="setup-section-body" *ngIf="isOpen('shipping')">
      <p class="setup-help-text">Configure your delivery and pickup options for customers.</p>
      <button pButton type="button" class="setup-save-btn" label="Shipping Configuration" (click)="goToShipping()"></button>
    </div>
  </div>

  <div class="setup-section" *ngIf="stripeStepNeeded" [class.open]="isOpen('stripe')">
    <div class="setup-section-header" (click)="toggleSection('stripe')">
      <div class="setup-section-head-left">
        <span class="setup-step-index">{{ stripeStepNumber }}.</span>
        <div>
          <div class="setup-section-title">Connect payment processing</div>
          <div class="setup-section-desc">Accept card payments and receive payouts.</div>
        </div>
      </div>
      <div class="setup-section-head-right">
        <span class="status-pill" [class.complete]="isStripeConnected" *ngIf="!fetchingStripeStatus">{{ isStripeConnected ? (stripAccountStatus || 'Connected') : (stripAccountStatus || 'Pending') }}</span>
        <span class="status-pill" *ngIf="fetchingStripeStatus">Please wait...</span>
        <i class="pi" [ngClass]="isOpen('stripe') ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
      </div>
    </div>
    <div class="setup-section-body" *ngIf="isOpen('stripe')">
      <p class="setup-help-text">
        Stripe is our secure payment partner. Connecting your account enables you to accept credit cards, debit cards and digital payments, with payouts sent directly to your bank account.
      </p>

      <div *ngIf="!isStripeConnected">
        <div class="flex align-items-center gap-2 mb-2">
          <i class="pi pi-arrow-right text-primary"></i>
          <span class="font-medium text-primary">Connect your Stripe account to get started</span>
        </div>
        <div class="setup-actions-row">
          <button
            pButton
            type="button"
            icon="pi pi-link"
            [loading]="isConnectStripeAccountLoading"
            [ngClass]="{'disabled': isConnectStripeAccountLoading || fetchingStripeStatus}"
            class="setup-save-btn"
            [label]="isConnectStripeAccountLoading ? 'Please wait...' : 'Connect Stripe Account'"
            (click)="openConnectStripeAccount()"
          ></button>
          <a class="contact-us-btn contact-us-btn-outline" href="mailto:support@lincsell.com?subject=Alternative%20payment%20method">Contact us</a>
        </div>
        <p class="setup-help-text mt-2" *ngIf="isConnectStripeAccountError && isConnectStripeAccountErrorText !== ''">
          {{ isConnectStripeAccountErrorText }}
        </p>
      </div>

      <div class="flex align-items-center gap-2" *ngIf="isStripeConnected">
        <i class="pi pi-check text-green-500"></i>
        <span class="font-medium text-green-600">All set! Your Stripe account is connected.</span>
      </div>
    </div>
  </div>

  <div class="setup-section" [class.open]="isOpen('card')" *ngIf="cardStepNeeded">
    <div class="setup-section-header" (click)="toggleSection('card')">
      <div class="setup-section-head-left">
        <span class="setup-step-index">{{ cardStepNumber }}.</span>
        <div>
          <div class="setup-section-title">Billing card</div>
          <div class="setup-section-desc">Add a card on file for your LincSell subscription billing.</div>
        </div>
      </div>
      <div class="setup-section-head-right">
        <span class="status-pill" [class.complete]="cardComplete">{{ cardComplete ? 'Complete' : 'Pending' }}</span>
        <i class="pi" [ngClass]="isOpen('card') ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
      </div>
    </div>
    <div class="setup-section-body" *ngIf="isOpen('card')">
      <p class="setup-help-text" *ngIf="!cardComplete">
        Add a card so we have a payment method on file for your subscription.
      </p>
      <app-stripe-add-card-button *ngIf="!cardComplete" label="Add card" (cardSaved)="onCardSaved($event)"></app-stripe-add-card-button>

      <div class="setup-card-on-file" *ngIf="cardComplete">
        <div class="setup-card-visual">
          <div class="setup-card-visual-top">
            <span class="setup-card-chip"><i class="pi pi-wifi"></i></span>
            <span class="setup-card-brand">{{ cardBrandLabel }}</span>
          </div>
          <div class="setup-card-number">
            <span class="setup-card-group setup-card-dots">••••</span>
            <span class="setup-card-group setup-card-dots">••••</span>
            <span class="setup-card-group setup-card-dots">••••</span>
            <span class="setup-card-group setup-card-last4">{{ cardLast4 || '••••' }}</span>
          </div>
          <div class="setup-card-visual-bottom" *ngIf="cardExpiryLabel">
            <span class="setup-card-exp-label">Valid thru</span>
            <span class="setup-card-exp-value">{{ cardExpiryLabel }}</span>
          </div>
        </div>
        <div class="setup-card-on-file-actions">
          <p class="setup-help-text setup-card-on-file-note">Your LincSell subscription is billed to this card.</p>
          <app-stripe-add-card-button label="Update card" (cardSaved)="onCardSaved($event)"></app-stripe-add-card-button>
        </div>
      </div>
    </div>
  </div>

  <a class="back-to-dashboard" [routerLink]="['/app/dashboard-v2']">&larr; Back to dashboard</a>
  </ng-container>
</div>

<p-dialog
  [(visible)]="onlineStoreInformationVisible"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [closable]="!isSavingOnlineStoreInformation"
  [closeOnEscape]="!isSavingOnlineStoreInformation"
  [dismissableMask]="false"
  [style]="{ width: '760px' }"
  styleClass="online-store-information-dialog"
>
  <ng-template pTemplate="header">
    <div class="online-store-dialog-heading">
      <h2>Choose your store template</h2>
      <p>Pick the template for your online store.</p>
    </div>
  </ng-template>

  <div class="online-store-dialog-body">
    <div class="online-store-modal-field">
      <span>Store template <span class="required">*</span></span>
      <div class="store-template-grid" role="radiogroup" aria-label="Store template">
        <button
          type="button"
          class="store-template-card"
          *ngFor="let tpl of storeTemplates"
          [class.selected]="pendingTemplateKey === tpl.key"
          [attr.aria-checked]="pendingTemplateKey === tpl.key"
          role="radio"
          (click)="requestTemplateSelection(tpl.key)"
        >
          <img [src]="tpl.image" [alt]="tpl.name + ' store template preview'" />
          <span class="store-template-card-copy">
            <span class="store-template-name-row">
              <strong>{{ tpl.name }}</strong>
              <span class="store-template-radio" [class.checked]="pendingTemplateKey === tpl.key"></span>
            </span>
            <small>{{ tpl.description }}</small>
            <a *ngIf="tpl.demoUrl" class="store-template-demo-link" [href]="tpl.demoUrl" target="_blank" rel="noopener" (click)="$event.stopPropagation()">View demo</a>
          </span>
        </button>
      </div>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="online-store-dialog-footer">
      <span>Choose carefully — your store template cannot be changed later.</span>
      <button
        pButton
        type="button"
        class="submit-online-store-button"
        label="Save"
        [loading]="isSavingOnlineStoreInformation"
        [disabled]="!pendingTemplateKey || isSavingOnlineStoreInformation"
        (click)="submitOnlineStoreInformation()"
      ></button>
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [(visible)]="customStoreRequestVisible"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [closable]="false"
  [closeOnEscape]="false"
  [dismissableMask]="false"
  [showHeader]="false"
  [style]="{ width: '360px' }"
  styleClass="custom-store-request-dialog"
>
  <div class="custom-store-request-content">
    <span class="custom-store-request-icon" aria-hidden="true">
      <i class="pi pi-clock"></i>
    </span>
    <div class="custom-store-request-copy">
      <h2>We have received your custom online store request</h2>
      <p>
        Our team is working on your online store and it will be ready in the next 3 business days.
        We will contact you<span *ngIf="customStoreContactEmail"> on email <strong>({{ customStoreContactEmail }})</strong></span>
        when it&rsquo;s ready.
      </p>
      <button
        pButton
        type="button"
        label="Got it"
        class="custom-store-request-dismiss"
        (click)="closeCustomStoreRequestConfirmation()"
      ></button>
    </div>
  </div>
</p-dialog>

<p-dialog
  [(visible)]="isTermsDialogVisible"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '640px' }"
  [breakpoints]="{ '960px': 'calc(100vw - 32px)' }"
  styleClass="domain-terms-dialog"
  header="Online Store Terms &amp; Conditions"
>
  <div class="terms-dialog-body">
    <h3>1. Prohibited Use</h3>
    <p>
      Your online store must not be used to sell, promote, or distribute illegal goods or services,
      engage in fraudulent activity, infringe on intellectual property rights, distribute malware, or
      otherwise violate applicable laws and regulations.
    </p>
    <h3>2. Restricted Products/Services</h3>
    <p>
      Certain product and service categories are restricted or require additional approval, including
      (without limitation) weapons and ammunition, controlled substances, counterfeit goods, adult
      content, tobacco and vaping products, gambling services, and financial or investment products.
      Contact support before listing items in a restricted category.
    </p>
    <h3>3. Policy Violations</h3>
    <p>
      Stores found in violation of these terms may be suspended or terminated without notice.
      Repeated or severe violations may also result in the loss of access to LincSell services.
      You are responsible for ensuring your store and its listings remain compliant at all times.
    </p>
  </div>
  <ng-template pTemplate="footer">
    <button
      pButton
      type="button"
      label="Close"
      (click)="closeTermsDialog()"
    ></button>
  </ng-template>
</p-dialog>
